import { useState } from "react";
import { Loader2, Link as LinkIcon, Plus, ClipboardPaste } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { parseUrl, parseText } from "@/lib/api";
import { RENTAL } from "@/constants/testIds";

export const UrlInputHero = ({ onAdded, onOpenManual, onNeedsBookmarklet }) => {
  const [mode, setMode] = useState("url"); // "url" | "text"
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const submitUrl = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) {
      toast.error("Paste a rental ad URL first");
      return;
    }
    setLoading(true);
    try {
      const r = await parseUrl(url.trim());
      toast.success(`Added: ${r.title || "New rental"}`);
      setUrl("");
      onAdded?.(r);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Structured "needs bookmarklet" response (RentFaster, Kijiji, etc.)
      if (detail && typeof detail === "object" && detail.code === "needs_bookmarklet") {
        onNeedsBookmarklet?.({ url: detail.url || url.trim(), host: detail.host });
        return;
      }
      // FB / IG / LinkedIn — bookmarklets blocked too, route to paste-text mode
      if (detail && typeof detail === "object" && detail.code === "needs_paste_text") {
        setMode("text");
        setSourceUrl(detail.url || url.trim());
        setUrl("");
        toast.info(
          `${detail.host} blocks scraping and bookmarklets. Copy the ad text below and we'll parse it.`,
          { duration: 8000 },
        );
        return;
      }
      const msg = (typeof detail === "string" ? detail : detail?.message) || err?.message || "Failed to parse URL";
      toast.error(msg, { duration: 7000 });
      if (typeof msg === "string" && /paste ad text/i.test(msg)) {
        setMode("text");
        setSourceUrl(url.trim());
      }
    } finally {
      setLoading(false);
    }
  };

  const submitText = async (e) => {
    e?.preventDefault?.();
    if (text.trim().length < 20) {
      toast.error("Paste the listing description (at least a sentence)");
      return;
    }
    setLoading(true);
    try {
      const r = await parseText({
        text: text.trim(),
        source_url: sourceUrl.trim(),
        image_url: imageUrl.trim(),
      });
      toast.success(`Added: ${r.title || "New rental"}`);
      setText("");
      setSourceUrl("");
      setImageUrl("");
      onAdded?.(r);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to parse text";
      toast.error(typeof msg === "string" ? msg : "Failed to parse text");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="max-w-5xl mx-auto px-6 lg:px-0 pt-14 pb-8">
      <div className="mb-7">
        <p className="overline mb-3">Step 01 — Drop a listing link</p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-stone-900 leading-[1.05]">
          Find the rental that
          <br />
          <span className="italic text-[#4A5D4E]">actually fits you.</span>
        </h1>
        <p className="text-stone-600 text-base sm:text-lg mt-5 max-w-2xl">
          Paste any rental ad URL. We'll extract the price, bedrooms, amenities,
          and a hero image — then let you slice and dice your shortlist.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex items-center gap-1 bg-white border border-stone-200 rounded-full p-1 w-fit mb-4 shadow-sm">
        <ModeButton
          active={mode === "url"}
          onClick={() => setMode("url")}
          icon={<LinkIcon size={14} strokeWidth={1.8} />}
          label="Paste URL"
          testid="mode-url-btn"
        />
        <ModeButton
          active={mode === "text"}
          onClick={() => setMode("text")}
          icon={<ClipboardPaste size={14} strokeWidth={1.8} />}
          label="Paste ad text"
          testid={RENTAL.pasteTextToggle}
        />
      </div>

      {mode === "url" ? (
        <form
          onSubmit={submitUrl}
          className="bg-white rounded-3xl border border-stone-200 p-2 shadow-[0_8px_32px_rgba(28,25,23,0.06)] flex items-center gap-2"
        >
          <div className="pl-5 text-stone-400">
            <LinkIcon size={20} strokeWidth={1.6} />
          </div>
          <Input
            data-testid={RENTAL.urlInput}
            type="text"
            placeholder="https://www.craigslist.org/… or zillow.com/… or any rental ad"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={loading}
            className="flex-1 border-none focus-visible:ring-0 focus-visible:ring-offset-0 text-base sm:text-lg h-14 bg-transparent placeholder:text-stone-400"
          />
          <Button
            data-testid={RENTAL.parseUrlBtn}
            type="submit"
            disabled={loading}
            className="bg-[#4A5D4E] hover:bg-[#3D4D40] text-[#F9F8F6] rounded-2xl px-7 h-14 font-medium tracking-wide"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Parsing…
              </>
            ) : (
              "Add Listing"
            )}
          </Button>
        </form>
      ) : (
        <form
          onSubmit={submitText}
          className="bg-white rounded-3xl border border-stone-200 p-5 shadow-[0_8px_32px_rgba(28,25,23,0.06)] space-y-3"
        >
          <p className="text-xs text-stone-500 leading-relaxed">
            For Facebook Marketplace, Instagram, or sites that block scrapers —
            copy the listing's description text and paste below. We'll still
            extract bedrooms, rent, amenities, etc.
          </p>
          <Textarea
            data-testid={RENTAL.pasteTextArea}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste the full rental ad description here…"
            disabled={loading}
            rows={6}
            className="bg-[#F9F8F6] border-stone-200 text-base resize-y"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              data-testid={RENTAL.pasteSourceUrl}
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="Source URL (optional)"
              className="bg-[#F9F8F6] border-stone-200"
            />
            <Input
              data-testid={RENTAL.pasteImageUrl}
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="Image URL (optional)"
              className="bg-[#F9F8F6] border-stone-200"
            />
          </div>
          <div className="flex justify-end">
            <Button
              data-testid={RENTAL.parseTextBtn}
              type="submit"
              disabled={loading}
              className="bg-[#4A5D4E] hover:bg-[#3D4D40] text-[#F9F8F6] rounded-2xl px-7 h-12 font-medium tracking-wide"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Parsing…
                </>
              ) : (
                "Parse & Add"
              )}
            </Button>
          </div>
        </form>
      )}

      <div className="mt-4 flex items-center gap-3">
        <span className="overline text-stone-400">Or</span>
        <button
          type="button"
          onClick={onOpenManual}
          data-testid={RENTAL.manualAddBtn}
          className="inline-flex items-center gap-2 text-sm font-medium text-stone-700 hover:text-[#4A5D4E] underline-offset-4 hover:underline transition-colors"
        >
          <Plus size={16} strokeWidth={1.8} />
          Add a rental manually
        </button>
      </div>
    </section>
  );
};

const ModeButton = ({ active, onClick, icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
      active
        ? "bg-[#4A5D4E] text-[#F9F8F6] shadow-sm"
        : "text-stone-600 hover:text-stone-900"
    }`}
  >
    {icon}
    {label}
  </button>
);

export default UrlInputHero;
